import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { useLocale } from "@/i18n/LocaleContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Calendar, MapPin, Clock, Check, X, Send, Inbox, ArrowLeft, Car } from "lucide-react";
import Navigation from "@/components/Navigation";

const mapsUrl = (loc) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;
const uberUrl = (loc) => `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(loc)}`;

export default function Meetups() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, appName } = useAppMode();
  const { t } = useLocale();
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMeetups = useCallback(async () => {
    try {
      const [sentRes, receivedRes] = await Promise.all([
        api.get("/meetups/sent"),
        api.get("/meetups/received")
      ]);
      setSentRequests(sentRes.data.requests);
      setReceivedRequests(receivedRes.data.requests);
    } catch (error) {
      console.error("Failed to load meetups:", error);
      toast.error("Failed to load meetups");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchMeetups();
  }, [fetchMeetups]);

  const handleRespond = async (requestId, status) => {
    try {
      await api.put(`/meetups/${requestId}/respond`, { status });
      toast.success(`Meetup ${status}!`);
      fetchMeetups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-500 text-xs">{t("common.pending")}</span>;
      case "accepted":
        return <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-500 text-xs">{t("common.accepted")}</span>;
      case "declined":
        return <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-500 text-xs">{t("common.declined")}</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="meetups-page">
      <Navigation />
      
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight">{t("meetups.title")}</h1>
            <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary" data-testid="meetups-mode-badge">
              {appName}
            </span>
          </div>

          <Tabs defaultValue="received" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 p-1 rounded-xl mb-6">
              <TabsTrigger 
                value="received" 
                className="rounded-lg data-[state=active]:bg-primary"
                data-testid="received-tab"
              >
                <Inbox className="w-4 h-4 mr-2" />
                {t("meetups.received")} ({receivedRequests.length})
              </TabsTrigger>
              <TabsTrigger 
                value="sent" 
                className="rounded-lg data-[state=active]:bg-primary"
                data-testid="sent-tab"
              >
                <Send className="w-4 h-4 mr-2" />
                {t("meetups.sent")} ({sentRequests.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="received">
              {receivedRequests.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Inbox className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">{t("meetups.none")}</h2>
                  <p className="text-muted-foreground">
                    {t("meetups.noneDesc")}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {receivedRequests.map((req) => (
                    <div key={req.request_id} className="glass-card p-5">
                      <div className="flex items-start gap-4">
                        <img
                          src={req.sender?.photos?.[0] || req.sender?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(req.sender?.name || "User")}&background=${avatarBg()}&color=fff&size=100`}
                          alt={req.sender?.name}
                          className="w-14 h-14 rounded-full object-cover cursor-pointer"
                          onClick={() => navigate(`/profile/${req.sender?.user_id}`)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 
                              className="font-semibold cursor-pointer hover:text-primary"
                              onClick={() => navigate(`/profile/${req.sender?.user_id}`)}
                            >
                              {req.sender?.name}
                            </h3>
                            {getStatusBadge(req.status)}
                          </div>
                          
                          <div className="space-y-1 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(req.proposed_date).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{req.proposed_location}</span>
                            </div>
                            {req.duration_hours ? (
                              <div className="flex items-center gap-2" data-testid={`meetup-duration-${req.request_id}`}>
                                <Clock className="w-4 h-4" />
                                <span>{req.duration_hours} {t("common.hours")}</span>
                              </div>
                            ) : null}
                            {req.status === "accepted" && req.proposed_location && (
                              <div className="flex items-center gap-4 pt-1">
                                <a href={mapsUrl(req.proposed_location)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`maps-link-${req.request_id}`}>
                                  <MapPin className="w-3.5 h-3.5" /> {t("common.viewMap")}
                                </a>
                                <a href={uberUrl(req.proposed_location)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`uber-link-${req.request_id}`}>
                                  <Car className="w-3.5 h-3.5" /> {t("common.getUber")}
                                </a>
                              </div>
                            )}
                          </div>

                          {req.message && (
                            <p className="text-sm bg-white/5 rounded-lg p-3 mb-3">
                              "{req.message}"
                            </p>
                          )}

                          {req.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => handleRespond(req.request_id, "accepted")}
                                data-testid={`accept-${req.request_id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                {t("common.accept")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                onClick={() => handleRespond(req.request_id, "declined")}
                                data-testid={`decline-${req.request_id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                {t("common.decline")}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sent">
              {sentRequests.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Send className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">{t("meetups.noneSent")}</h2>
                  <p className="text-muted-foreground mb-6">
                    {t("meetups.noneSentDesc")}
                  </p>
                  <Button onClick={() => navigate("/discover")}>
                    {t("meetups.browse")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentRequests.map((req) => (
                    <div key={req.request_id} className="glass-card p-5">
                      <div className="flex items-start gap-4">
                        <img
                          src={req.receiver?.photos?.[0] || req.receiver?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(req.receiver?.name || "User")}&background=${avatarBg()}&color=fff&size=100`}
                          alt={req.receiver?.name}
                          className="w-14 h-14 rounded-full object-cover cursor-pointer"
                          onClick={() => navigate(`/profile/${req.receiver?.user_id}`)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 
                              className="font-semibold cursor-pointer hover:text-primary"
                              onClick={() => navigate(`/profile/${req.receiver?.user_id}`)}
                            >
                              {req.receiver?.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              {req.test_run && (
                                <span className="px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-xs" data-testid={`test-run-chip-${req.request_id}`}>
                                  🧪 {t("common.testRun")}
                                </span>
                              )}
                              {getStatusBadge(req.status)}
                            </div>
                          </div>
                          
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(req.proposed_date).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{req.proposed_location}</span>
                            </div>
                            {req.duration_hours ? (
                              <div className="flex items-center gap-2" data-testid={`meetup-duration-${req.request_id}`}>
                                <Clock className="w-4 h-4" />
                                <span>{req.duration_hours} {t("common.hours")}</span>
                              </div>
                            ) : null}
                            {req.status === "accepted" && req.proposed_location && (
                              <div className="flex items-center gap-4 pt-1">
                                <a href={mapsUrl(req.proposed_location)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`maps-link-${req.request_id}`}>
                                  <MapPin className="w-3.5 h-3.5" /> {t("common.viewMap")}
                                </a>
                                <a href={uberUrl(req.proposed_location)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`uber-link-${req.request_id}`}>
                                  <Car className="w-3.5 h-3.5" /> {t("common.getUber")}
                                </a>
                              </div>
                            )}
                          </div>

                          {req.message && (
                            <p className="text-sm bg-white/5 rounded-lg p-3 mt-3">
                              "{req.message}"
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
